<template>
  <v-container fluid class="pa-6">
    <!-- Cabecera -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <h1 class="headline font-weight-bold mb-1">Plantillas de Mantenimiento</h1>
        <p class="subtitle-1 grey--text text--darken-1">Configuración de mantenimientos preventivos</p>
      </v-col>
      <v-col cols="12" md="6" class="d-flex align-center justify-md-end">
        <v-btn color="primary" large class="rounded-lg" @click="openDialog()">
          <v-icon left>mdi-plus</v-icon>
          Nueva Plantilla
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabla de Plantillas -->
    <v-card outlined class="rounded-xl mt-2">
      <v-card-title class="pa-4">
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Buscar por máquina o frecuencia..."
          outlined dense hide-details style="max-width: 400px" clearable></v-text-field>
      </v-card-title>

      <v-data-table :headers="headers" :items="plantillas" :search="search" class="bg-transparent">
        <!-- Columna Máquina -->
        <template v-slot:item.maquina="{ item }">
          <span class="font-weight-medium">{{ item.maquina }}</span>
        </template>

        <!-- Columna Tareas (Resumen) -->
        <template v-slot:item.tareas="{ item }">
          <v-chip x-small outlined color="info">
            {{ item.tareas ? item.tareas.length : 0 }} tareas
          </v-chip>
        </template>

        <!-- Columna Estado -->
        <template v-slot:item.active="{ item }">
          <v-chip :color="item.active ? 'success' : 'error'" small dark>
            {{ item.active ? 'Activa' : 'Anulada' }}
          </v-chip>
        </template>

        <!-- Columna Fecha Inicio -->
        <template v-slot:item.fecha_inicio="{ item }">
          {{ formatDate(item.fecha_inicio) }}
        </template>

        <!-- Acciones -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <v-btn icon small color="primary" @click="openDialog(item)">
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn icon small :color="item.active ? 'error' : 'success'" @click="toggleActive(item)"
              :title="item.active ? 'Anular plantilla' : 'Activar plantilla'">
              <v-icon>{{ item.active ? 'mdi-minus-circle-outline' : 'mdi-check-circle-outline' }}</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal Crear/Editar -->
    <v-dialog v-model="dialog" max-width="850px" persistent scrollable>
      <v-card class="rounded-xl overflow-hidden">
        <!-- Cabecera Estilizada -->
        <v-toolbar flat color="primary" dark>
          <v-icon left>mdi-file-document-edit-outline</v-icon>
          <v-toolbar-title class="font-weight-bold">
            {{ isEditing ? 'Editar Plantilla' : 'Nueva Plantilla' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-form ref="formRef" v-model="isFormValid">
            
            <!-- SECCIÓN 1: Identificación -->
            <div class="section-title mb-4">
              <v-icon small color="primary" class="mr-2">mdi-information-outline</v-icon>
              Información General
            </div>
            <v-row dense>
              <v-col cols="12" md="12">
                <v-text-field 
                  v-model="editedItem.nombre" 
                  label="Nombre de la plantilla" 
                  outlined 
                  dense
                  prepend-inner-icon="mdi-tag-outline"
                  placeholder="Ej: Mantenimiento Preventivo Mensual"
                  :rules="[v => !!v || 'Campo obligatorio']" 
                  required 
                />
              </v-col>

              <!-- Solo Ramos: Cliente -->
              <v-col cols="12" md="6" v-if="esRamos">
                <v-autocomplete 
                  v-model="editedItem.id_empresa" 
                  :items="clientes" 
                  item-text="nombre" 
                  item-value="id_cliente"
                  label="Cliente" 
                  placeholder="Buscar cliente..."
                  chips 
                  outlined 
                  dense
                  @change="onClientChange"
                  prepend-inner-icon="mdi-office-building"
                />
              </v-col>

              <!-- Máquina -->
              <v-col cols="12" md="6">
                <v-select 
                  v-model="editedItem.maquina_id" 
                  :items="maquinas" 
                  item-text="nombre" 
                  item-value="id"
                  :disabled="isEditing" 
                  label="Máquina" 
                  outlined 
                  dense
                  prepend-inner-icon="mdi-robot-industrial"
                  :rules="[v => !!v || 'Campo obligatorio']"
                  required
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-autocomplete 
                  v-model="editedItem.ids_operarios" 
                  :items="operarios" 
                  item-text="username" 
                  item-value="id"
                  label="Operarios asignados" 
                  multiple 
                  chips 
                  outlined 
                  dense
                  small-chips
                  prepend-inner-icon="mdi-account-hard-hat"
                />
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <!-- SECCIÓN 2: Planificación -->
            <div class="section-title mb-4">
              <v-icon small color="primary" class="mr-2">mdi-calendar-clock</v-icon>
              Planificación y Horarios
            </div>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-select 
                  v-model="editedItem.frecuencia_id" 
                  :items="frecuencias" 
                  item-text="label" 
                  item-value="id"
                  label="Frecuencia" 
                  outlined 
                  dense
                  prepend-inner-icon="mdi-repeat"
                  :rules="[v => !!v || 'Campo obligatorio']" 
                  required 
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field 
                  v-model="editedItem.tiempo_previsto" 
                  label="Tiempo Previsto" 
                  type="number"
                  outlined 
                  dense
                  suffix="horas" 
                  prepend-inner-icon="mdi-clock-outline"
                  :rules="[v => !!v || 'Campo obligatorio']" 
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field 
                  v-model="editedItem.fecha_inicio" 
                  label="Fecha de inicio" 
                  type="date"
                  outlined 
                  dense
                  prepend-inner-icon="mdi-calendar-start"
                  :rules="[v => !!v || 'Campo obligatorio']" 
                />
              </v-col>

              <v-col cols="12">
                <v-card flat outlined class="pa-3 grey lighten-5 rounded-lg">
                  <div class="text-caption font-weight-bold mb-2 grey--text text--darken-2">
                    GESTIÓN DE DÍAS NO HÁBILES / FESTIVOS:
                  </div>
                  <v-radio-group v-model="editedItem.logica_festivo" row hide-details class="mt-0">
                    <v-radio :value="0" color="error">
                      <template v-slot:label>
                        <span class="text-body-2">No crear trabajo</span>
                      </template>
                    </v-radio>
                    <v-radio :value="1" color="success">
                      <template v-slot:label>
                        <span class="text-body-2">Mover al siguiente día hábil</span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </v-card>
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <!-- SECCIÓN 3: Tareas -->
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="section-title">
                <v-icon small color="primary" class="mr-2">mdi-format-list-checks</v-icon>
                Tareas a Realizar
              </div>
              <v-btn color="primary" small depressed @click="addTask" class="rounded-pill">
                <v-icon left small>mdi-plus</v-icon>
                Añadir Tarea
              </v-btn>
            </div>

            <v-card flat outlined class="rounded-lg">
              <div v-if="editedItem.tareas.length === 0" class="text-center py-8">
                <v-icon size="48" color="grey lighten-2">mdi-clipboard-text-outline</v-icon>
                <div class="grey--text mt-2">No hay tareas configuradas</div>
              </div>

              <v-list class="pa-0">
                <v-scroll-x-transition group>
                  <div v-for="(tarea, index) in editedItem.tareas" :key="'task-' + index">
                    <v-list-item class="px-4 py-2">
                      <v-list-item-avatar color="primary lighten-4" size="24" class="white--text caption font-weight-bold">
                        {{ index + 1 }}
                      </v-list-item-avatar>
                      
                      <v-list-item-content>
                        <v-text-field 
                          v-model="editedItem.tareas[index]" 
                          placeholder="Describa la actividad a realizar..."
                          hide-details
                          dense
                          flat
                          solo
                          class="task-input"
                          :rules="[v => !!v || 'La tarea no puede estar vacía']" 
                        />
                      </v-list-item-content>

                      <v-list-item-action>
                        <v-btn icon color="error lighten-2" @click="removeTask(index)">
                          <v-icon small>mdi-delete-outline</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider v-if="index < editedItem.tareas.length - 1"></v-divider>
                  </div>
                </v-scroll-x-transition>
              </v-list>
            </v-card>
            
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4 grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-2" @click="closeDialog" class="text-none">
            Cancelar
          </v-btn>
          <v-btn 
            color="primary" 
            depressed 
            width="180" 
            height="44"
            class="rounded-lg text-none"
            :loading="isSaving"
            :disabled="!isFormValid || editedItem.tareas.length === 0" 
            @click="savePlantilla"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            Guardar Plantilla
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="notify.show" :color="notify.color" :timeout="notify.timeout" elevation="24" top>
      <div class="d-flex align-center">
        <v-icon dark class="mr-2">{{ notify.icon }}</v-icon>
        {{ notify.text }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="notify.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios'
import { mapActions, mapState } from 'vuex';

export default {
  name: 'PlantillasMantenimiento',
  data() {
    return {
      operarios: [],

      // Sistema de notificaciones
      notify: {
        show: false,
        text: '',
        color: 'success',
        icon: 'mdi-check-circle',
        timeout: 3000
      },
      // Estado UI
      search: '',
      dialog: false,
      isSaving: false,
      isFormValid: false,
      isEditing: false,
      // Datos
      plantillas: [],
      maquinas: [],
      frecuencias: [],
      // Cabeceras Tabla (V2 usa text/value)
      headers: [
        { text: 'Nombre', value: 'nombre', align: 'start' },
        { text: 'Máquina', value: 'maquina', align: 'start' },
        { text: 'Frecuencia', value: 'frecuencia_nombre', align: 'start' },
        { text: 'Tiempo', value: 'tiempo_previsto', align: 'center' },
        { text: 'Tareas', value: 'tareas', align: 'center', sortable: false },
        { text: 'Estado', value: 'active', align: 'center', width: '150px' },
        { text: 'Inicio', value: 'fecha_inicio', align: 'start' },
        { text: 'Acciones', value: 'actions', align: 'end', sortable: false },
      ],
      // Modelo de datos
      defaultItem: {
        id: null,
        nombre: '',
        maquina_id: null,
        frecuencia_id: null,
        tiempo_previsto: null,
        fecha_inicio: new Date().toISOString().substr(0, 10),
        logica_festivo: 0,
        tareas: []
      },
      editedItem: {
        tareas: []
      }
    }
  },
  computed: {
    ...mapState(["clientes", "esRamos"]),
    // Centralizamos la obtención del ID de empresa
    idEmpresa() {
      return localStorage.getItem("clienteSeleccionado");
    }
  },
  mounted() {
    this.initData();
  },
  methods: {
    ...mapActions(["getData"]),
    async loadUsuarios() {
      this.loading = true;
      try {
        const response = await this.getData({ path: "django/portal/get_all_users" });
        this.operarios = response.users.filter((c) => c.id_cliente == this.idEmpresa) || response;
      } catch (err) {
        this.showNotify('Error al cargar usuarios', 'error');
      } finally {
        this.loading = false;
      }
    },

    showNotify(text, color = 'success') {
      this.notify.text = text;
      this.notify.color = color;
      if (color === 'error') this.notify.icon = 'mdi-alert-circle';
      else if (color === 'info') this.notify.icon = 'mdi-information';
      else this.notify.icon = 'mdi-check-circle';
      this.notify.show = true;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const d = new Date(dateString);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },
    async initData() {
      if (!this.idEmpresa) return;

      await Promise.all([
        this.loadPlantillas(),
        this.loadMaquinas(),
        this.loadFrecuencias(),
        this.loadUsuarios(),
      ]);
    },
    async loadPlantillas() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/plantillas`, {
          params: { id_empresa: this.idEmpresa } // Enviado como query param
        });
        this.plantillas = res.data.plantillas || res.data;
      } catch (e) {
        this.showNotify('Error cargando plantillas', 'error');
      }
    },
    async loadMaquinas() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/maquinas-activas`, {
          params: { id_empresa: this.idEmpresa } // Enviado como query param
        });
        this.maquinas = res.data.maquinas || res.data;
      } catch (e) {
        this.showNotify('Error cargando máquinas', 'error');
      }
    },
    async onClientChange(){
      try {
        const res = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/maquinas-activas`, {
          params: { id_empresa: this.editedItem.id_empresa /*this.editedItem.id_empresa != 11956 ? this.editedItem.id_empresa : 2000*/ } // Enviado como query param
        });
        this.maquinas = res.data.maquinas || res.data;
      } catch (e) {
        this.showNotify('Error cargando máquinas', 'error');
      }
    },
    async loadFrecuencias() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/frecuencias-activas`, {
          params: { id_empresa: this.idEmpresa } // Enviado como query param
        });
        const data = res.data.frecuencias || res.data;
        this.frecuencias = data.map(f => ({
          ...f,
          label: `${f.descripcion} - ${f.dias} días`
        }));
      } catch (e) {
        this.showNotify('Error cargando frecuencias', 'error');
      }
    },
    async toggleActive(item) {
      try {
        const nuevoEstado = !item.active;
        await axios.patch(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/plantillas/${item.id}/active`, {
          active: nuevoEstado,
          id_empresa: this.idEmpresa // Enviamos ID para validación de seguridad
        });
        this.showNotify(nuevoEstado ? 'Plantilla activada' : 'Plantilla anulada', 'info');
        this.loadPlantillas();
      } catch (e) {
        this.showNotify('No se pudo cambiar el estado', 'error');
      }
    },
    addTask() {
      this.editedItem.tareas.push('');
    },
    removeTask(index) {
      this.editedItem.tareas.splice(index, 1);
    },
    openDialog(item = null) {
      if (item) {
        this.isEditing = true;
        this.editedItem = JSON.parse(JSON.stringify(item));
        this.editedItem.fecha_inicio = item.fecha_inicio.substring(0, 10);
        this.editedItem.logica_festivo = item.logica_festivo ? 1 : 0;
      } else {
        this.isEditing = false;
        this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
        this.editedItem.tareas = [''];
      }
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      if (this.$refs.formRef) this.$refs.formRef.resetValidation();
    },
    async savePlantilla() {
      if (!this.isFormValid) return;
      this.isSaving = true;

      // Preparamos el objeto a enviar incluyendo el id_empresa
      const payload = {
        ...this.editedItem,
        id_empresa: this.idEmpresa,
      };

      try {
        if (this.isEditing) {
          await axios.put(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/plantillas/${this.editedItem.id}`, payload);
          this.showNotify('Plantilla actualizada', 'success');
        } else {
          await axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/plantillas`, payload);
          this.showNotify('Plantilla creada correctamente', 'success');
        }
        await this.loadPlantillas();
        this.closeDialog();
      } catch (error) {
        this.showNotify('Error al guardar la plantilla', 'error');
      } finally {
        this.isSaving = false;
      }
    }
  }
}
</script>

<style scoped>
/* Clases personalizadas */
.rounded-xl {
  border-radius: 24px !important;
}

.rounded-lg {
  border-radius: 12px !important;
}
.section-title {
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #555;
  letter-spacing: 0.5px;
}

.task-input >>> .v-input__slot {
  background: transparent !important;
  padding: 0 !important;
}

.rounded-xl {
  border-radius: 16px !important;
}

/* Efecto hover en las tareas para facilitar la edición */
.v-list-item:hover {
  background-color: #f8f9fa;
}
</style>